import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import firestoreService from '../services/firestoreService';
import AdminHeader from '../components/admin/AdminHeader';
import AdminNavigation from '../components/admin/AdminNavigation';
import './Analytics.css';

const Analytics = () => {
    const [analyticsData, setAnalyticsData] = useState({
        userStats: {},
        auditStats: {},
        featureUsage: [],
        errorStats: {},
        timeSeriesData: []
    });
    const [loading, setLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState('7d');
    const navigate = useNavigate();

    const loadAnalyticsData = async () => {
        try {
            setLoading(true);
            const data = await firestoreService.getAnalyticsData(selectedPeriod);
            setAnalyticsData(data);
        } catch (error) {
            console.error('Error loading analytics data:', error);
            // Fallback to default values
            setAnalyticsData({
                userStats: { totalUsers: 0, activeUsers: 0, newUsers: 0, retentionRate: 0 },
                auditStats: { totalLogs: 0, todayLogs: 0, errorLogs: 0 },
                featureUsage: [],
                errorStats: {},
                timeSeriesData: []
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Check authentication
        const isAuthenticated = localStorage.getItem('adminAuth');
        if (!isAuthenticated) {
            navigate('/admin');
            return;
        }

        loadAnalyticsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, selectedPeriod]);

    const formatNumber = (num) => {
        return num.toLocaleString();
    };

    const formatPercentage = (num) => {
        return num.toFixed(2);
    };

    const getPercentageColor = (percentage) => {
        if (percentage >= 80) return '#28a745';
        if (percentage >= 60) return '#ffc107';
        return '#dc3545';
    };

    if (loading) {
        return (
            <div className="analytics">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading analytics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="analytics">
            <AdminHeader title="Analytics Dashboard">
                <div className="period-selector">
                    <select
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                        className="period-select"
                    >
                        <option value="1d">Last 24 Hours</option>
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="90d">Last 90 Days</option>
                    </select>
                </div>
            </AdminHeader>

            <AdminNavigation />

            <main className="admin-main">
                {/* Key Metrics */}
                <div className="metrics-grid">
                    <div className="metric-card">
                        <div className="metric-icon users-icon">👥</div>
                        <div className="metric-content">
                            <h3>{formatNumber(analyticsData.userStats?.totalUsers || 0)}</h3>
                            <p>Total Users</p>
                            <span className="metric-change positive">
                                +{analyticsData.userStats?.newUsers || 0} this week
                            </span>
                        </div>
                    </div>

                    <div className="metric-card">
                        <div className="metric-icon active-icon">✅</div>
                        <div className="metric-content">
                            <h3>{formatNumber(analyticsData.userStats?.activeUsers || 0)}</h3>
                            <p>Active Users</p>
                            <span className="metric-change positive">
                                {formatPercentage(analyticsData.userStats?.retentionRate || 0)}% retention
                            </span>
                        </div>
                    </div>

                    <div className="metric-card">
                        <div className="metric-icon logs-icon">📊</div>
                        <div className="metric-content">
                            <h3>{formatNumber(analyticsData.auditStats?.totalLogs || 0)}</h3>
                            <p>Total Audit Logs</p>
                            <span className="metric-change">
                                {formatNumber(analyticsData.auditStats?.todayLogs || 0)} today
                            </span>
                        </div>
                    </div>

                    <div className="metric-card">
                        <div className="metric-icon error-icon">⚠️</div>
                        <div className="metric-content">
                            <h3>{formatNumber(analyticsData.auditStats?.errorLogs || 0)}</h3>
                            <p>Error Logs</p>
                            <span className="metric-change negative">
                                {analyticsData.auditStats?.criticalLogs || 0} critical
                            </span>
                        </div>
                    </div>
                </div>

                {/* Feature Usage */}
                <div className="analytics-section">
                    <h2>Feature Usage</h2>
                    <div className="feature-usage-grid">
                        {(analyticsData.featureUsage || []).map((feature, index) => (
                            <div key={index} className="feature-card">
                                <div className="feature-header">
                                    <h4>{feature.feature}</h4>
                                    <span className="usage-percentage">{formatPercentage(feature.usage)}%</span>
                                </div>
                                <div className="usage-bar">
                                    <div
                                        className="usage-fill"
                                        style={{ width: `${feature.usage}%` }}
                                    ></div>
                                </div>
                                <div className="feature-stats">
                                    <span>{formatNumber(feature.users)} users</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Error Analysis */}
                <div className="analytics-section">
                    <h2>Error Analysis</h2>
                    <div className="error-stats-grid">
                        {(analyticsData.errorStats?.errorTypes || []).map((error, index) => (
                            <div key={index} className="error-card">
                                <div className="error-header">
                                    <h4>{error.type}</h4>
                                    <span className="error-count">{error.count}</span>
                                </div>
                                <div className="error-bar">
                                    <div
                                        className="error-fill"
                                        style={{
                                            width: `${error.percentage}%`,
                                            backgroundColor: getPercentageColor(100 - error.percentage)
                                        }}
                                    ></div>
                                </div>
                                <div className="error-percentage">
                                    {formatPercentage(error.percentage)}% of total errors
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Time Series Chart */}
                <div className="analytics-section">
                    <h2>Activity Over Time</h2>
                    <div className="chart-container">
                        <div className="chart-header">
                            <h3>Daily Activity</h3>
                            <div className="chart-legend">
                                <div className="legend-item">
                                    <span className="legend-color users-color"></span>
                                    Users
                                </div>
                                <div className="legend-item">
                                    <span className="legend-color logs-color"></span>
                                    Audit Logs
                                </div>
                                <div className="legend-item">
                                    <span className="legend-color errors-color"></span>
                                    Errors
                                </div>
                            </div>
                        </div>
                        <div className="chart-content">
                            <div className="chart-bars">
                                {analyticsData.timeSeriesData.map((data, index) => (
                                    <div key={index} className="chart-bar-group">
                                        <div className="chart-bar users-bar" style={{ height: `${(data.users / 1300) * 100}%` }}></div>
                                        <div className="chart-bar logs-bar" style={{ height: `${(data.logs / 1500) * 100}%` }}></div>
                                        <div className="chart-bar errors-bar" style={{ height: `${(data.errors / 25) * 100}%` }}></div>
                                        <div className="chart-label">{new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Export Options */}
                <div className="analytics-section">
                    <h2>Export Data</h2>
                    <div className="export-options">
                        <button className="export-btn">
                            📊 Export Analytics Report
                        </button>
                        <button className="export-btn">
                            📋 Export User Data
                        </button>
                        <button className="export-btn">
                            📝 Export Audit Logs
                        </button>
                        <button className="export-btn">
                            ⚠️ Export Error Report
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Analytics;
